// The user webclient shell — the 3CX-style "second surface" for end users.
// Left rail (Chat / Calls / Contacts / Voicemail / Meet / Settings) + a header
// with the user chip and, for admins, a switch back to the Admin console.

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { isAdminRole } from './role';
import { BrandLogo } from '../components/BrandLogo';
import { useActiveTenant, setActiveTenant, ALL } from '../lib/tenantScope';

type Tab = { to: string; label: string; icon: JSX.Element };

const icon = (d: string) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

const TABS: Tab[] = [
  { to: 'chat',      label: 'Chat',      icon: icon('M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z') },
  { to: 'calls',     label: 'Calls',     icon: icon('M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z') },
  { to: 'contacts',  label: 'Contacts',  icon: icon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75') },
  { to: 'voicemail', label: 'Voicemail', icon: icon('M5.5 18a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zM18.5 18a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zM5.5 18h13') },
  { to: 'meet',      label: 'Meet',      icon: icon('M23 7l-7 5 7 5V7zM14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z') },
  { to: 'settings',  label: 'Settings',  icon: icon('M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z') },
];

export default function WebclientLayout() {
  const me = api.me();
  const nav = useNavigate();
  const qc = useQueryClient();
  const admin = isAdminRole(me);
  // Only System Admins / resellers can actually cross tenants (matches the
  // server privilege in pickDomain/scopeOf). Department admins have an admin
  // console but are pinned to their own department, so the switcher would be a
  // no-op for them — don't show it.
  const canSwitchClients = me?.role === 'super_admin' || me?.role === 'reseller';
  const who = me?.username || 'me';

  // Sys-admin client switcher: which department the webclient is currently
  // viewing. Reuses the admin console's tenant scope (auto-injected on API
  // calls); changing it re-scopes Chat / Calls / Voicemail / Directory.
  const tenants = useQuery({ queryKey: ['wc-tenants'], queryFn: () => api.tenants.list(), enabled: canSwitchClients });
  const active = useActiveTenant();
  const onSwitch = (v: string) => {
    setActiveTenant(v);
    qc.invalidateQueries();   // re-fetch every tab against the newly-selected client
  };
  // "No selection" is NOT nothing — the server pins every call to the admin's
  // own department. Say so, and make an actual cross-client selection loud so
  // an admin never texts a customer as the wrong business without noticing.
  const tName = (uuid?: string | null) => {
    const t = (tenants.data || []).find((x: any) => x.domain_uuid === uuid);
    return t ? (t.domain_description || t.domain_name) : '';
  };
  const ownName = tName(me?.domain_uuid);
  const scoped = active !== ALL;
  const scopedName = scoped ? (tName(active) || 'unknown client') : '';

  const logout = async () => {
    try { await api.logout(); } catch {}
    try { localStorage.removeItem('stratus_token'); } catch {}
    nav('/login', { replace: true });
  };

  return (
    <div className="h-screen flex bg-bg text-textPrimary">
      {/* rail */}
      <nav className="w-[76px] shrink-0 flex flex-col items-center gap-1 py-3 border-r border-border bg-surface">
        <div className="mb-3"><BrandLogo iconOnly size={26} /></div>
        {TABS.map(t => (
          <NavLink key={t.to} to={t.to} title={t.label}
            className={({ isActive }) =>
              `w-[60px] py-2 rounded-lg flex flex-col items-center gap-1 text-[10px] transition-colors ${
                isActive ? 'bg-elevated text-primary' : 'text-textSecondary hover:bg-elevated hover:text-textPrimary'
              }`}>
            {t.icon}
            <span>{t.label}</span>
          </NavLink>
        ))}
        <div className="flex-1" />
        {admin && (
          // Full-page navigation to the site root (a real <a>, NOT a client-side
          // NavLink). The admin console and webclient are the same SPA; a client-side
          // to="/" did not reliably land admins on the console (the index route's
          // isAdminRole(api.me()) gate, esp. right after first login / MFA), so force a
          // fresh bootstrap at "/" which resolves the admin surface correctly.
          <a href="/" title="Admin console"
            className="w-[60px] py-2 rounded-lg flex flex-col items-center gap-1 text-[10px] text-textSecondary hover:bg-elevated hover:text-textPrimary">
            {icon('M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z')}
            <span>Admin</span>
          </a>
        )}
      </nav>

      {/* main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 shrink-0 flex items-center justify-between px-5 border-b border-border bg-surface">
          <div className="font-semibold tracking-tight">Stratus</div>
          <div className="flex items-center gap-3">
            {canSwitchClients && (
              <label className="flex items-center gap-2 text-xs text-textSecondary">
                {scoped ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-2.5 py-1 text-xs font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                    Viewing client
                  </span>
                ) : 'Client'}
                <select
                  className={`input py-1 text-sm ${scoped ? 'border-primary text-primary font-medium bg-primary/10' : ''}`}
                  value={active}
                  title={scoped ? `All tabs are scoped to ${scopedName} — you are acting as this client` : `No client selected — you are viewing your own department${ownName ? ` (${ownName})` : ''}`}
                  onChange={e => onSwitch(e.target.value)}>
                  <option value={ALL}>{ownName ? `My department (${ownName})` : 'My department'}</option>
                  {/* stored scope not in the list (still loading / dept deleted): keep the
                      select truthful rather than silently rendering the first option */}
                  {scoped && !(tenants.data || []).some((t: any) => t.domain_uuid === active) && (
                    <option value={active}>{scopedName}</option>
                  )}
                  {(tenants.data || []).map((t: any) => (
                    <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="text-right leading-tight">
              <div className="text-sm font-medium">Ext {who}</div>
              <div className="text-[10px] text-textSecondary">{me?.role || 'user'}</div>
            </div>
            <button onClick={logout} title="Sign out"
              className="text-textSecondary hover:text-textPrimary p-2 rounded-lg hover:bg-elevated">
              {icon('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9')}
            </button>
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
